import { CompetitorCrawlReportView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { findArticleIdByKeyword } from '@/features/articles/api';
import { useArticleMutations } from '@/features/articles/hooks/useArticles';
import { useCompetitorCrawl, useCompetitorMutations } from '../hooks/useCompetitors';

export function CompetitorCrawlReportPage() {
  const { competitorId = '' } = useParams();
  const navigate = useNavigate();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } =
    useCompetitorCrawl(competitorId);
  const { recrawl, exportCrawlPdf } = useCompetitorMutations();
  const { draft } = useArticleMutations();

  const handleGenerateArticle = useCallback(
    (topic: string) => {
      draft.mutate(
        { targetKeyword: topic },
        {
          onSuccess: async (job) => {
            const jobRecord = job as Record<string, unknown>;
            const fromJob = String(
              jobRecord.article_id ??
                jobRecord.articleId ??
                jobRecord.entity_id ??
                jobRecord.entityId ??
                '',
            ).trim();
            if (fromJob) {
              navigate(`/articles/${fromJob}`);
              return;
            }
            const articleId = await findArticleIdByKeyword(topic);
            if (articleId) {
              navigate(`/articles/${articleId}`);
              return;
            }
            navigate('/content');
          },
        },
      );
    },
    [draft, navigate],
  );

  const subjects = data?.subject ? [data.subject] : undefined;

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
      isEmpty={data === null}
    >
      <CompetitorCrawlReportView
        {...viewProps({
          subjects,
          report: data ?? undefined,
          loading: isFetching || recrawl.isPending || draft.isPending || exportCrawlPdf.isPending,
          onRecrawl: () => recrawl.mutate(competitorId),
          onExport: () => exportCrawlPdf.mutate(competitorId),
          onGenerateArticle: handleGenerateArticle,
        })}
      />
    </AsyncBoundary>
  );
}
